import { useEffect, useMemo, useState } from 'react';
import { useAuthKit } from '@/contexts/AuthProvider';

/**
 * Cross-checks the MineTwin-side Workforce roster against WorkOS-side
 * identity state — backs the per-row badge in
 * `src/components/Workforce/Workforce.tsx` so admins can tell at a
 * glance whether "pending" means "WorkOS member, hasn't opened the
 * portal yet" or "WorkOS invite still outstanding" or "neither".
 *
 * Backed by the `GET /api/workos-roster?organizationId=org_...` SWA
 * Function (api/src/functions/workos-roster.ts). The Function paginates
 * WorkOS itself; this hook just fetches once on mount + polls every
 * 60s while mounted. Auth uses the same `X-Workos-Authorization`
 * pattern as `/api/invite-user` and `/api/databricks-sql` (Azure SWA
 * overwrites the standard `Authorization` header on the way in).
 *
 * Local-dev note: the SWA Function only exists in the deployed
 * `api/` Function App; running `npm run dev` against an unstarted
 * `func start` returns 404/503. In that case the hook surfaces a
 * dev-only error string and Workforce falls back to the bare
 * MineTwin "Signed in / Pending" pill.
 */

type Invitation = {
  email: string;
  state: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string | null;
};

type RosterPayload = {
  members: string[];
  invitations: Invitation[];
};

export type WorkosRosterState = {
  /** True once the first fetch has resolved (success OR error). */
  isReady: boolean;
  /** Last fetch error, suitable for an inline diagnostic. `null` on success. */
  error: string | null;
  /** Lower-case emails of WorkOS org members. */
  memberEmails: Set<string>;
  /** Lower-case emails -> outstanding pending invitation row. */
  pendingInvitesByEmail: Map<string, Invitation>;
  /** Force-refresh the next time the polling interval ticks (or
   *  immediately on call). */
  refresh: () => void;
};

const POLL_INTERVAL_MS = 60_000;

export function useWorkosRoster(): WorkosRosterState {
  const { user, organizationId, getAccessToken } = useAuthKit();
  const [payload, setPayload] = useState<RosterPayload | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!user || !organizationId) {
        // Stay in loading state until AuthKit resolves the session.
        return;
      }
      try {
        const workosJwt = await getAccessToken();
        if (cancelled) return;
        const url = `/api/workos-roster?organizationId=${encodeURIComponent(organizationId)}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Workos-Authorization': `Bearer ${workosJwt}`,
          },
        });
        if (cancelled) return;
        if (res.status === 404 || res.status === 503) {
          setError(
            import.meta.env.DEV
              ? 'API not running locally (start `func start` in api/ to enable the WorkOS roster check)'
              : 'WorkOS roster endpoint not reachable (deploy may be incomplete)',
          );
          setIsReady(true);
          return;
        }
        const body = (await res.json().catch(() => ({}))) as
          | RosterPayload
          | { error: string };
        if (!res.ok || 'error' in body) {
          setError(('error' in body && body.error) || `HTTP ${res.status}`);
          setIsReady(true);
          return;
        }
        setPayload(body);
        setError(null);
        setIsReady(true);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setIsReady(true);
      }
    }

    void run();
    const id = setInterval(() => void run(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, organizationId, getAccessToken, refreshTick]);

  const memberEmails = useMemo(
    () => new Set<string>(payload?.members ?? []),
    [payload],
  );
  const pendingInvitesByEmail = useMemo(() => {
    const m = new Map<string, Invitation>();
    for (const inv of payload?.invitations ?? []) {
      m.set(inv.email, inv);
    }
    return m;
  }, [payload]);

  return {
    isReady,
    error,
    memberEmails,
    pendingInvitesByEmail,
    refresh: () => setRefreshTick((n) => n + 1),
  };
}
